import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Zap, Camera, ChefHat, UtensilsCrossed } from 'lucide-react';
import { AIUsageService, AIFeatureType } from '../../lib/aiUsageService';
import { supabase } from '../../lib/supabase';

interface FeatureUsageData {
  used: number;
  limit: number;
  plan_type: string;
  resets_on: string;
  can_use: boolean;
  reason: string;
}

interface UsageStats {
  image_scan: FeatureUsageData | null;
  recipe_generation: FeatureUsageData | null;
  meal_analysis: FeatureUsageData | null;
}

const AIUsageSectionMobile = () => {
  const [usageStats, setUsageStats] = useState<UsageStats>({
    image_scan: null,
    recipe_generation: null,
    meal_analysis: null
  });
  const [loading, setLoading] = useState(true);

  // Feature configuration
  const features = [
    {
      key: 'image_scan' as AIFeatureType,
      icon: Camera,
      title: 'Image Scanning',
      shortTitle: 'Images'
    },
    {
      key: 'recipe_generation' as AIFeatureType,
      icon: ChefHat,
      title: 'Recipe Generation',
      shortTitle: 'Recipes'
    },
    {
      key: 'meal_analysis' as AIFeatureType,
      icon: UtensilsCrossed,
      title: 'Meal Analysis',
      shortTitle: 'Meals'
    }
  ];

  useEffect(() => {
    const fetchAllUsageData = async () => {
      try {
        setLoading(true);
        
        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        if (!user?.id) return;

        // Fetch usage data for each feature
        const statsPromises = features.map(async (feature) => {
          try {
            const limits = await AIUsageService.checkFeatureLimit(user.id, feature.key);
            return { feature: feature.key, data: limits };
          } catch (error) {
            console.error(`Error fetching ${feature.key} usage:`, error);
            return { feature: feature.key, data: null };
          }
        });

        const results = await Promise.all(statsPromises);
        
        // Transform results into usageStats format
        const newStats: UsageStats = {
          image_scan: null,
          recipe_generation: null,
          meal_analysis: null
        };

        results.forEach(({ feature, data }) => {
          if (data) {
            newStats[feature] = data;
          }
        });

        setUsageStats(newStats);
      } catch (error) {
        console.error('Error in fetchAllUsageData:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllUsageData();
  }, []);

  // Calculate days until reset
  const getDaysUntilReset = (resetDate: string): string => {
    const now = new Date();
    const reset = new Date(resetDate);
    const diffTime = reset.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 0) return 'Resets today';
    if (diffDays === 1) return 'Resets tomorrow';
    return `Resets in ${diffDays} days`;
  };

  // Loading state
  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
        <div className="animate-pulse">
          {/* Header skeleton */}
          <div className="flex items-center gap-2 mb-4">
            <div className="w-6 h-6 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32"></div>
          </div>
          
          {/* Feature items skeleton */}
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-3 mb-3 last:mb-0">
              <div className="w-4 h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
              <div className="flex-1">
                <div className="flex justify-between items-center mb-1">
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-12"></div>
                </div>
                <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Individual feature item component (horizontal layout)
  const UsageStatsItem: React.FC<{
    feature: typeof features[0];
    data: FeatureUsageData | null;
  }> = ({ feature, data }) => {
    if (!data) return null;

    const progress = data.limit > 0 ? data.used / data.limit : 0;
    const isAtLimit = progress >= 1;
    const isNearLimit = progress >= 0.8;

    return (
      <div className="flex items-center gap-3 mb-3 last:mb-0">
        {/* Icon */}
        <feature.icon size={16} className="text-gray-600 dark:text-gray-400 flex-shrink-0" />
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Title and Usage */}
          <div className="flex justify-between items-center mb-1">
            <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
              {feature.shortTitle}
            </span>
            <span className={`text-sm font-semibold ${
              isAtLimit 
                ? 'text-red-600 dark:text-red-400' 
                : 'text-gray-900 dark:text-white'
            }`}>
              {data.used}/{data.limit}
            </span>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${Math.min(progress * 100, 100)}%` }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className={`h-1.5 rounded-full ${
                isAtLimit 
                  ? 'bg-red-500' 
                  : isNearLimit 
                    ? 'bg-yellow-500' 
                    : 'bg-orange-500'
              }`}
            />
          </div>
        </div>
      </div>
    );
  };

  const planType = Object.values(usageStats).find(data => data !== null)?.plan_type || 'free';
  const resetDate = Object.values(usageStats).find(data => data !== null)?.resets_on;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-amber-100 dark:bg-amber-900 rounded-full flex items-center justify-center">
            <Zap size={14} className="text-amber-600 dark:text-amber-400" />
          </div>
          <h3 className="text-sm font-bold text-gray-900 dark:text-white">
            AI Usage This Month
          </h3>
        </div>
        <span className={`text-xs px-2 py-1 rounded-full font-medium capitalize ${
          planType === 'free' 
            ? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
            : planType === 'pro'
              ? 'bg-orange-100 text-orange-600 dark:bg-orange-900 dark:text-orange-300'
              : 'bg-purple-100 text-purple-600 dark:bg-purple-900 dark:text-purple-300'
        }`}>
          {planType} Plan
        </span>
      </div>

      {/* Individual Feature Items */}
      {features.map((feature) => (
        <UsageStatsItem
          key={feature.key}
          feature={feature}
          data={usageStats[feature.key]}
        />
      ))}

      {/* Reset Date */}
      {resetDate && (
        <div className="text-center mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {getDaysUntilReset(resetDate)}
          </span>
        </div>
      )}

      {/* Show message if no data */}
      {!Object.values(usageStats).some(data => data !== null) && (
        <div className="text-center py-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No usage data available
          </p>
        </div>
      )}
    </motion.div>
  );
};

export default AIUsageSectionMobile;